import { Fragment, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import StatusBadge from "../components/StatusBadge";
import { AttachmentList } from "../components/AttachmentControl";
import MissingReceiptModal from "../components/MissingReceiptModal";
import { gbp, dateShort, dateTimeShort, emptyDraftDeleteDate } from "../lib/format";
import type { ExpenseLine, NewLineInput } from "../api/types";

const TODAY = new Date().toISOString().slice(0, 10);

const blankLine: NewLineInput = {
  expense_date: TODAY,
  category_id: 0,
  description: "",
  merchant_name: "",
  amount: 0,
};

export default function ClaimDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: claim, isLoading, error } = useQuery({
    queryKey: ["claim", id],
    queryFn: () => api.expenses.get(id!),
    enabled: !!id,
  });
  const { data: categories } = useQuery({ queryKey: ["categories"], queryFn: api.categories });
  const { data: mileageRates } = useQuery({ queryKey: ["mileage-rates"], queryFn: api.mileageRates });
  const { data: mileageLocations } = useQuery({ queryKey: ["mileage-locations"], queryFn: api.mileageLocations });

  const [draftLine, setDraftLine] = useState<NewLineInput>(blankLine);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingReceipt, setPendingReceipt] = useState<File | null>(null);
  const newReceiptInput = useRef<HTMLInputElement>(null);
  const [declaringLine, setDeclaringLine] = useState<ExpenseLine | null>(null);
  // Mileage From/To dropdown selection: a home name, "__other__", or "" (unset)
  const [fromSel, setFromSel] = useState("");
  const [toSel, setToSel] = useState("");
  const resetLineForm = () => { setDraftLine(blankLine); setFromSel(""); setToSel(""); lastCalcKey.current = ""; setAutoCalc({ state: "idle" }); setPendingReceipt(null); setEditingId(null); };

  // Look up the matching mileage rate for the picked category + miles
  const selectedCategory = categories?.find((c) => c.id === draftLine.category_id);
  const isMileage = !!selectedCategory?.is_mileage;
  const vehicleType = (draftLine.mileage_vehicle_type ?? "car") as "car" | "company_car" | "motorbike" | "bicycle";
  const miles = Number(draftLine.mileage_miles ?? 0);
  const matchedRate = mileageRates?.find(
    (r) =>
      r.vehicle_type === vehicleType &&
      miles >= r.tier_min_miles &&
      (r.tier_max_miles == null || miles <= r.tier_max_miles),
  );
  const ratePence = matchedRate?.rate_pence ?? 0;
  const calculatedAmount = isMileage ? Math.round((miles * ratePence)) / 100 : draftLine.amount;

  // VAT — assumes draftLine.amount is gross. Compute VAT from the picked rate.
  // User can override the rate via dropdown OR override the calculated VAT amount directly.
  const vatRate = Number(draftLine.vat_rate ?? selectedCategory?.default_vat_rate ?? 0);
  const grossAmt = Number(draftLine.amount ?? 0);
  const autoVat = vatRate > 0 ? Math.round((grossAmt * vatRate / (100 + vatRate)) * 100) / 100 : 0;
  const vatAmt = draftLine.vat_amount != null ? Number(draftLine.vat_amount) : autoVat;
  const netAmt = Math.max(0, Math.round((grossAmt - vatAmt) * 100) / 100);

  // Hard per-category amount cap (e.g. Food & Household £5 / £15)
  const capAmount = !isMileage && selectedCategory?.cap_amount != null ? Number(selectedCategory.cap_amount) : null;
  const overCap = capAmount != null && Number(draftLine.amount) > capAmount;

  // Auto-calculate mileage from From/To postcodes via Azure Maps (editable result).
  const [autoCalc, setAutoCalc] = useState<{ state: "idle" | "loading" | "done" | "error"; msg?: string }>({ state: "idle" });
  const lastCalcKey = useRef<string>("");
  const doDistance = (from: string, to: string) => {
    setAutoCalc({ state: "loading" });
    api.mileageDistance(from, to)
      .then((r) => {
        setDraftLine((d) => ({ ...d, mileage_miles: r.miles }));
        setAutoCalc({ state: "done", msg: `Locked to ${r.miles} miles from the postcodes. Change From / To (or Recalculate) to adjust.` });
      })
      .catch((e) => setAutoCalc({ state: "error", msg: `Couldn't auto-calculate (${(e as Error).message}). Enter the miles manually.` }));
  };
  useEffect(() => {
    if (!isMileage) return;
    const from = (draftLine.mileage_origin_postcode ?? "").trim();
    const to = (draftLine.mileage_destination_postcode ?? "").trim();
    if (from.length < 5 || to.length < 5) return; // wait for full postcodes
    const key = `${from}|${to}`;
    if (key === lastCalcKey.current) return;
    lastCalcKey.current = key;
    doDistance(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMileage, draftLine.mileage_origin_postcode, draftLine.mileage_destination_postcode]);

  const addLine = useMutation({
    mutationFn: (override?: NewLineInput) => api.expenses.addLine(id!, override ?? draftLine),
    onSuccess: async (data) => {
      if (pendingReceipt && data?.id) {
        try { await api.expenses.uploadAttachment(id!, pendingReceipt, data.id); } catch { /* claimant can still attach after saving */ }
      }
      qc.invalidateQueries({ queryKey: ["claim", id] });
      resetLineForm();
      setAdding(false);
    },
  });

  const updateLine = useMutation({
    mutationFn: (payload: NewLineInput) => api.expenses.updateLine(id!, editingId!, payload),
    onSuccess: async (data) => {
      if (pendingReceipt && data?.id) {
        try { await api.expenses.uploadAttachment(id!, pendingReceipt, data.id); } catch { /* can still attach after saving */ }
      }
      qc.invalidateQueries({ queryKey: ["claim", id] });
      resetLineForm();
      setAdding(false);
    },
  });

  // Open the shared line form pre-filled with an existing line, in edit mode.
  const startEdit = (l: ExpenseLine) => {
    setEditingId(l.id);
    setDraftLine({
      expense_date: (l.expense_date ?? TODAY).slice(0, 10),
      category_id: l.category_id,
      description: l.description,
      merchant_name: l.merchant_name,
      amount: Number(l.amount),
      vat_rate: l.vat_rate,
      vat_amount: l.vat_amount,
      person_supported_id: l.person_supported_id,
      cost_centre_id: l.cost_centre_id,
      mileage_miles: l.mileage_miles,
      mileage_vehicle_type: (l.mileage_vehicle_type as "car" | "company_car" | "motorbike" | "bicycle" | null) ?? null,
      mileage_passenger_count: l.mileage_passenger_count,
      mileage_origin_postcode: l.mileage_origin_postcode,
      mileage_destination_postcode: l.mileage_destination_postcode,
      mileage_origin_home: l.mileage_origin_home,
      mileage_destination_home: l.mileage_destination_home,
      mileage_round_trip: l.mileage_round_trip,
    });
    setFromSel(l.mileage_origin_home ? l.mileage_origin_home : (l.mileage_origin_postcode ? "__other__" : ""));
    setToSel(l.mileage_destination_home ? l.mileage_destination_home : (l.mileage_destination_postcode ? "__other__" : ""));
    // Seed the auto-calc guard so the saved miles are not overwritten on open.
    lastCalcKey.current = `${(l.mileage_origin_postcode ?? "").trim()}|${(l.mileage_destination_postcode ?? "").trim()}`;
    setAutoCalc({ state: "idle" });
    setPendingReceipt(null);
    setAdding(true);
  };

  const deleteLine = useMutation({
    mutationFn: (lineId: string) => api.expenses.deleteLine(id!, lineId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", id] }),
  });

  const submit = useMutation({
    mutationFn: () => api.expenses.submit(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", id] }),
  });

  const recall = useMutation({
    mutationFn: () => api.expenses.recall(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", id] }),
  });

  const reopen = useMutation({
    mutationFn: () => api.expenses.reopen(id!),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["claim", id] }),
  });

  const deleteClaim = useMutation({
    mutationFn: () => api.expenses.delete(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      navigate("/", { replace: true });
    },
  });

  if (isLoading) return <div className="text-slate-500">Loading…</div>;
  if (error) return <div className="text-red-600 text-sm">{(error as Error).message}</div>;
  if (!claim) return <div className="text-slate-500">Not found</div>;

  const isDraft = claim.status === "draft";
  const isSubmitted = claim.status === "submitted";
  // Non-mileage lines must each have a receipt before submit.
  const missingReceiptLines = claim.lines.filter(
    (l) => !l.is_mileage && !claim.attachments.some((a) => a.line_id === l.id),
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between no-print">
        <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Back to my claims</Link>
        <div className="flex items-center gap-4">
          {isDraft && (
            <button
              onClick={() => {
                if (confirm(`Delete draft ${claim.request_number}? This can't be undone.`)) {
                  deleteClaim.mutate();
                }
              }}
              disabled={deleteClaim.isPending}
              className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              {deleteClaim.isPending ? "Deleting…" : "Delete draft"}
            </button>
          )}
          <button
            onClick={() => window.print()}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Save as PDF / Print
          </button>
        </div>
      </div>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-liaise-navy">{claim.request_number}</h1>
          <div className="text-slate-600 mt-2 flex items-center gap-3">
            <StatusBadge status={claim.status} />
            <span>{claim.cost_centre_name}</span>
            <span>·</span>
            <span>Created {dateShort(claim.created_at)}</span>
          </div>
          {claim.status_reason && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800">
              <strong>Rejection reason:</strong> {claim.status_reason}
            </div>
          )}
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-500">Total</div>
          <div className="text-3xl font-semibold text-liaise-navy">{gbp(Number(claim.total_amount))}</div>
          {Number(claim.total_vat) > 0 && (
            <div className="text-sm text-slate-500">incl. VAT {gbp(Number(claim.total_vat))}</div>
          )}
        </div>
      </div>

      <div className="text-sm text-slate-600">
        Manager: <span className="font-medium">{claim.manager_email ?? "—"}</span>
        {claim.manager_overridden && <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">overridden</span>}
      </div>

      {/* Lines */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-medium text-liaise-navy">Lines ({claim.lines.length})</h2>
          {isDraft && !adding && (
            <button onClick={() => setAdding(true)} className="text-liaise-navy font-medium text-sm hover:underline">
              + Add line
            </button>
          )}
        </div>

        {claim.lines.length === 0 && !adding && (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">
            No lines yet. {isDraft && "Add at least one before submitting."}
            {isDraft && (
              <div className="mt-4 mx-auto max-w-md text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left">
                This draft is empty. If no line is added it will be deleted automatically on {emptyDraftDeleteDate(claim.created_at)}. Nothing is owed and no claim has been made.
              </div>
            )}
          </div>
        )}

        {claim.lines.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-6 py-3 font-medium">Date</th>
                <th className="px-6 py-3 font-medium">Category</th>
                <th className="px-6 py-3 font-medium">Description</th>
                <th className="px-6 py-3 font-medium">Merchant</th>
                <th className="px-6 py-3 font-medium text-right">Amount</th>
                {isDraft && <th className="px-6 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {claim.lines.map((l) => {
                const lineAttachments = claim.attachments.filter((a) => a.line_id === l.id);
                return (
                  <Fragment key={l.id}>
                    <tr className={l.receipt_flag_reason ? "bg-red-50" : undefined}>
                      <td className="px-6 py-3 text-slate-600">{dateShort(l.expense_date)}</td>
                      <td className="px-6 py-3">{l.category_name}</td>
                      <td className="px-6 py-3 text-slate-600">{l.description ?? <span className="text-slate-400 italic">—</span>}</td>
                      <td className="px-6 py-3 text-slate-600">{l.merchant_name ?? <span className="text-slate-400 italic">—</span>}</td>
                      <td className="px-6 py-3 text-right font-medium">{gbp(Number(l.amount))}</td>
                      {isDraft && (
                        <td className="px-6 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={() => startEdit(l)}
                            className="text-liaise-navy text-xs hover:underline mr-3"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteLine.mutate(l.id)}
                            className="text-red-600 text-xs hover:underline"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                    <tr className="border-t-0">
                      <td colSpan={isDraft ? 6 : 5} className="px-6 pb-4 -mt-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {l.receipt_flag_reason && (
                            <span className="text-xs text-red-800 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                              ⚠ Receipt issue: {l.receipt_flag_reason}
                            </span>
                          )}
                          {l.is_mileage && (
                            <span className="text-xs text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                              🚗 {l.mileage_miles ?? 0} miles
                              {(l.mileage_origin_home || l.mileage_origin_postcode) &&
                                ` · ${l.mileage_origin_home || l.mileage_origin_postcode} → ${l.mileage_destination_home || l.mileage_destination_postcode || "?"}`}
                              {l.mileage_vehicle_type && ` · ${l.mileage_vehicle_type.replace("_", " ")}`}
                              {l.mileage_rate_pence != null && ` · ${l.mileage_rate_pence}p/mile`}
                            </span>
                          )}
                          <AttachmentList
                            requestId={claim.id}
                            lineId={l.id}
                            attachments={lineAttachments}
                            canEdit={isDraft}
                          />
                          {!l.is_mileage && lineAttachments.length === 0 && (
                            <>
                              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                                Receipt required
                              </span>
                              {isDraft && (
                                <button
                                  onClick={() => setDeclaringLine(l)}
                                  className="text-xs text-liaise-navy hover:underline font-medium"
                                  title="Lost the receipt and can't get a duplicate? Complete the missing receipt declaration (Expenses Policy, Appendix 1)."
                                >
                                  Missing receipt?
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        {adding && categories && (
          <div className="border-t border-slate-200 bg-slate-50 px-6 py-5 space-y-3">
            {editingId && (
              <p className="text-sm font-medium text-liaise-navy">Editing line</p>
            )}
            <p className="text-xs text-slate-500">
              {isMileage
                ? "Enter miles travelled — amount calculates automatically from the active mileage rate."
                : "Enter the total you paid (including VAT). The VAT split will appear below the row once you enter an amount."}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <input
                type="date"
                value={draftLine.expense_date}
                onChange={(e) => setDraftLine({ ...draftLine, expense_date: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2"
              />
              <select
                value={draftLine.category_id || ""}
                onChange={(e) => {
                  const cat = categories.find((c) => c.id === Number(e.target.value));
                  // Reset mileage and VAT fields when category changes — defaults take over.
                  setFromSel(""); setToSel(""); lastCalcKey.current = ""; setAutoCalc({ state: "idle" });
                  if (cat?.is_mileage) {
                    setDraftLine({ ...draftLine, category_id: cat.id, merchant_name: "", mileage_vehicle_type: "car", mileage_miles: 0, mileage_origin_postcode: null, mileage_destination_postcode: null, mileage_origin_home: null, mileage_destination_home: null, vat_rate: null, vat_amount: null });
                  } else {
                    setDraftLine({ ...draftLine, category_id: Number(e.target.value), mileage_miles: null, mileage_vehicle_type: null, mileage_origin_postcode: null, mileage_destination_postcode: null, mileage_origin_home: null, mileage_destination_home: null, vat_rate: null, vat_amount: null });
                  }
                }}
                className="border border-slate-300 rounded-lg px-3 py-2"
              >
                <option value="">Category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              {isMileage ? (
                <>
                  <select
                    value={vehicleType}
                    onChange={(e) =>
                      setDraftLine({ ...draftLine, mileage_vehicle_type: e.target.value as "car" | "company_car" | "motorbike" | "bicycle" })
                    }
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  >
                    <option value="car">Car</option>
                    <option value="company_car">Company car</option>
                    <option value="motorbike">Motorbike</option>
                    <option value="bicycle">Bicycle</option>
                  </select>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={draftLine.mileage_miles ?? ""}
                    onChange={(e) => setDraftLine({ ...draftLine, mileage_miles: e.target.value === "" ? null : Number(e.target.value) })}
                    readOnly={autoCalc.state === "done"}
                    title={autoCalc.state === "done" ? "Auto-calculated from the postcodes — change From / To to adjust." : "Miles travelled"}
                    placeholder="Miles"
                    className={`border rounded-lg px-3 py-2 ${autoCalc.state === "done" ? "bg-slate-100 text-slate-600 border-slate-200 cursor-not-allowed" : "border-slate-300"}`}
                  />
                  <div className="flex flex-col gap-1">
                    <select
                      value={fromSel}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFromSel(v);
                        const loc = mileageLocations?.find((l) => l.name === v);
                        setDraftLine({ ...draftLine, mileage_origin_postcode: v === "__other__" ? null : (loc?.postcode ?? null), mileage_origin_home: (v === "__other__" || v === "") ? null : v });
                      }}
                      aria-label="From location"
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    >
                      <option value="">From…</option>
                      {mileageLocations?.map((l) => (
                        <option key={l.name} value={l.name}>{l.name} ({l.postcode})</option>
                      ))}
                      <option value="__other__">Other (type postcode)</option>
                    </select>
                    {fromSel === "__other__" && (
                      <input
                        type="text"
                        maxLength={8}
                        value={draftLine.mileage_origin_postcode ?? ""}
                        onChange={(e) => setDraftLine({ ...draftLine, mileage_origin_postcode: e.target.value.toUpperCase() || null })}
                        placeholder="From postcode"
                        aria-label="From postcode"
                        className="border border-slate-300 rounded-lg px-3 py-2 uppercase"
                      />
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <select
                      value={toSel}
                      onChange={(e) => {
                        const v = e.target.value;
                        setToSel(v);
                        const loc = mileageLocations?.find((l) => l.name === v);
                        setDraftLine({ ...draftLine, mileage_destination_postcode: v === "__other__" ? null : (loc?.postcode ?? null), mileage_destination_home: (v === "__other__" || v === "") ? null : v });
                      }}
                      aria-label="To location"
                      className="border border-slate-300 rounded-lg px-3 py-2"
                    >
                      <option value="">To…</option>
                      {mileageLocations?.map((l) => (
                        <option key={l.name} value={l.name}>{l.name} ({l.postcode})</option>
                      ))}
                      <option value="__other__">Other (type postcode)</option>
                    </select>
                    {toSel === "__other__" && (
                      <input
                        type="text"
                        maxLength={8}
                        value={draftLine.mileage_destination_postcode ?? ""}
                        onChange={(e) => setDraftLine({ ...draftLine, mileage_destination_postcode: e.target.value.toUpperCase() || null })}
                        placeholder="To postcode"
                        aria-label="To postcode"
                        className="border border-slate-300 rounded-lg px-3 py-2 uppercase"
                      />
                    )}
                  </div>
                  <input
                    type="text"
                    value={draftLine.description ?? ""}
                    onChange={(e) => setDraftLine({ ...draftLine, description: e.target.value })}
                    placeholder="Reason for travel"
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <div className="border border-slate-200 bg-white rounded-lg px-3 py-2 text-right text-slate-700 font-medium">
                    {gbp(calculatedAmount)}
                    <span className="block text-xs text-slate-500 font-normal">
                      {miles > 0 && matchedRate ? `${miles} × ${ratePence}p` : "0 miles"}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={draftLine.merchant_name ?? ""}
                    onChange={(e) => setDraftLine({ ...draftLine, merchant_name: e.target.value })}
                    placeholder="Merchant"
                    className="border border-slate-300 rounded-lg px-3 py-2"
                  />
                  <input
                    type="text"
                    value={draftLine.description ?? ""}
                    onChange={(e) => setDraftLine({ ...draftLine, description: e.target.value })}
                    placeholder="Description"
                    className="border border-slate-300 rounded-lg px-3 py-2 md:col-span-1"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={capAmount ?? undefined}
                    value={draftLine.amount || ""}
                    onChange={(e) => setDraftLine({ ...draftLine, amount: Number(e.target.value) })}
                    placeholder={capAmount != null ? `Total (max £${capAmount.toFixed(2)})` : "Total inc. VAT"}
                    aria-label="Total amount including VAT"
                    title="Enter the total amount paid including VAT — VAT split below will calculate automatically."
                    className={`border rounded-lg px-3 py-2 text-right ${overCap ? "border-red-400 bg-red-50" : "border-slate-300"}`}
                  />
                </>
              )}
            </div>
            {isMileage && autoCalc.state !== "idle" && (
              <div className={`text-xs ${autoCalc.state === "error" ? "text-red-600" : "text-slate-500"}`}>
                {autoCalc.state === "loading" ? "Calculating distance from postcodes…" : autoCalc.msg}
                {autoCalc.state !== "loading" &&
                  (draftLine.mileage_origin_postcode ?? "").trim().length >= 5 &&
                  (draftLine.mileage_destination_postcode ?? "").trim().length >= 5 && (
                    <button
                      type="button"
                      onClick={() => {
                        lastCalcKey.current = "";
                        doDistance((draftLine.mileage_origin_postcode ?? "").trim(), (draftLine.mileage_destination_postcode ?? "").trim());
                      }}
                      className="ml-2 underline text-liaise-navy"
                    >
                      Recalculate
                    </button>
                  )}
              </div>
            )}
            {capAmount != null && (
              <div className={`text-xs ${overCap ? "text-red-600 font-medium" : "text-slate-500"}`}>
                {selectedCategory?.cap_message || `Maximum £${capAmount.toFixed(2)} for this category.`}
                {overCap && ` — reduce the amount to £${capAmount.toFixed(2)} or less.`}
              </div>
            )}
            {!isMileage && grossAmt > 0 && (
              <div className="flex flex-wrap items-center gap-3 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2">
                <span className="text-slate-500">VAT</span>
                <select
                  value={vatRate}
                  onChange={(e) => setDraftLine({ ...draftLine, vat_rate: Number(e.target.value), vat_amount: null })}
                  className="border border-slate-300 rounded px-2 py-1 text-sm"
                >
                  <option value={0}>0% (zero-rated)</option>
                  <option value={5}>5% (reduced)</option>
                  <option value={20}>20% (standard)</option>
                </select>
                <span className="text-slate-500">·</span>
                <span className="text-slate-500">VAT amount</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={draftLine.vat_amount ?? autoVat.toFixed(2)}
                  onChange={(e) => setDraftLine({ ...draftLine, vat_amount: e.target.value === "" ? null : Number(e.target.value) })}
                  className="border border-slate-300 rounded px-2 py-1 text-sm w-24 text-right"
                />
                <span className="text-slate-500">·</span>
                <span className="text-slate-700">Net <span className="font-medium">{gbp(netAmt)}</span></span>
                <span className="text-slate-300">/</span>
                <span className="text-slate-700">Gross <span className="font-medium">{gbp(grossAmt)}</span></span>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={newReceiptInput}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) setPendingReceipt(f); e.currentTarget.value = ""; }}
              />
              <button
                type="button"
                onClick={() => newReceiptInput.current?.click()}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-liaise-navy bg-liaise-navy-50 border border-liaise-navy-200 rounded-full px-3 py-1 hover:bg-liaise-navy-200 transition"
              >
                📎 {pendingReceipt ? "Change receipt" : "Attach receipt"}
              </button>
              {pendingReceipt && (
                <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                  {pendingReceipt.name}
                  <button type="button" onClick={() => setPendingReceipt(null)} className="text-red-600 hover:underline ml-1">remove</button>
                </span>
              )}
              <span className="text-xs text-slate-400">optional, you can also add it after saving</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  // Mileage: send calculated amount + rate. Non-mileage: send the
                  // computed VAT split so unset fields don't store as NULL → 0.
                  const payload = isMileage
                    ? { ...draftLine, amount: calculatedAmount, mileage_rate_pence: ratePence }
                    : { ...draftLine, vat_rate: vatRate, vat_amount: vatAmt };
                  if (editingId) updateLine.mutate(payload as any);
                  else addLine.mutate(payload as any);
                }}
                disabled={
                  !draftLine.category_id ||
                  (isMileage ? !miles || !matchedRate : !draftLine.amount || overCap) ||
                  addLine.isPending || updateLine.isPending
                }
                className="bg-liaise-navy text-white font-medium px-4 py-2 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50 transition"
              >
                {editingId
                  ? (updateLine.isPending ? "Saving…" : "Save changes")
                  : (addLine.isPending ? "Adding…" : "Add line")}
              </button>
              <button onClick={() => { setAdding(false); resetLineForm(); }} className="text-slate-600 px-4 py-2 hover:underline">
                Cancel
              </button>
              {(addLine.error || updateLine.error) && (
                <span className="text-red-600 text-sm">{((addLine.error || updateLine.error) as Error).message}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-6 py-4 shadow-sm no-print">
        <div className="text-sm text-slate-600">
          {isDraft && claim.lines.length === 0 && `Add at least one line, then you can submit for approval. Empty drafts are deleted automatically after 3 days (this one on ${emptyDraftDeleteDate(claim.created_at)}).`}
          {isDraft && claim.lines.length > 0 && missingReceiptLines > 0 &&
            `${missingReceiptLines} line${missingReceiptLines > 1 ? "s" : ""} still need a receipt before you can submit. Mileage lines don't need one.`}
          {isDraft && claim.lines.length > 0 && missingReceiptLines === 0 && "Ready to submit. Once submitted, your manager will be notified."}
          {isSubmitted && "Submitted — awaiting your manager's approval. You can recall it before they action."}
          {claim.status === "manager_approved" && "Manager approved — payroll team will process this for payment."}
          {claim.status === "payroll_approved" && "Approved for payment. Look out for the payment in your next pay run."}
          {claim.status === "paid" && `Paid on ${dateShort(claim.paid_at)}.`}
          {claim.status === "rejected" && "Rejected. Edit the lines as needed and resubmit (will start fresh approval)."}
        </div>
        <div className="flex gap-2">
          {isDraft && claim.lines.length > 0 && (
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => submit.mutate()}
                disabled={submit.isPending || missingReceiptLines > 0}
                title={missingReceiptLines > 0 ? "Attach a receipt to every non-mileage line first" : undefined}
                className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
              >
                {submit.isPending ? "Submitting…" : "Submit for approval"}
              </button>
              {submit.error && <span className="text-xs text-red-600 max-w-xs text-right">{(submit.error as Error).message}</span>}
            </div>
          )}
          {isSubmitted && (
            <button
              onClick={() => recall.mutate()}
              disabled={recall.isPending}
              className="bg-white border border-slate-300 text-slate-700 font-medium px-5 py-2.5 rounded-lg hover:bg-slate-50"
            >
              Recall
            </button>
          )}
          {claim.status === "rejected" && (
            <button
              onClick={() => reopen.mutate()}
              disabled={reopen.isPending}
              className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50"
            >
              {reopen.isPending ? "Reopening…" : "Edit and resubmit"}
            </button>
          )}
        </div>
      </div>

      {/* History */}
      {(claim.approvals.length > 0 || claim.manager_overridden) && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h2 className="font-medium text-liaise-navy mb-4">Activity</h2>
          <ul className="space-y-2 text-sm">
            {claim.manager_overridden && (
              <li className="flex justify-between text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span>
                  <span className="font-medium text-amber-800">Manager overridden</span>
                  {" by "}
                  <span className="text-slate-700">{(claim as any).manager_override_set_by_email ?? "unknown"}</span>
                  {(claim as any).manager_override_reason && (
                    <span className="block text-xs italic text-slate-600 mt-1">"{(claim as any).manager_override_reason}"</span>
                  )}
                </span>
                {(claim as any).manager_override_set_at && (
                  <span className="text-xs text-slate-500">{dateTimeShort((claim as any).manager_override_set_at)}</span>
                )}
              </li>
            )}
            {claim.approvals.map((a, i) => (
              <li key={i} className="flex justify-between text-slate-600">
                <span>
                  <span className="font-medium text-slate-700 capitalize">{a.action}</span>
                  {" by "}
                  <span className="text-slate-700">{a.actor_email}</span>
                  <span className="text-xs ml-2 text-slate-500">({a.stage})</span>
                  {a.reason && <span className="block text-xs italic text-slate-500">"{a.reason}"</span>}
                </span>
                <span className="text-xs text-slate-500">{dateTimeShort(a.acted_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {declaringLine && (
        <MissingReceiptModal
          requestId={claim.id}
          line={declaringLine}
          onClose={() => setDeclaringLine(null)}
        />
      )}
    </div>
  );
}
